import React, { useCallback, useEffect, useState } from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaHandshake,
  FaSpinner,
  FaUser,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import bannerSbf from "../../assets/credenciamento2026/banner2026-credenciamento.png";
import ilustBaiana from "../../assets/credenciamento2026/baiana-2026.png";
import footerCred2026 from "../../assets/credenciamento2026/footer-2026.png";
import TokenInvalido from "../participantes/tokenInvalido";
import {
  concluirEscolhaConteudos,
  listarConteudosAgenda,
  validarTokenConteudos,
} from "./credenciamentoConteudosApi";
import { idsConteudosRegulares } from "./conteudosAgendaUtils";
import PassoResumo from "./PassoResumo";
import PassoConteudos from "./PassoConteudos";
import PassoMatchMeet from "./PassoMatchMeet";
import PassoConfirmacao from "./PassoConfirmacao";
import "../credenciamento2026/credenciamento2026.css";
import "./credenciamentoConteudos.css";

const STEP_RESUMO = 0;
const STEP_CONTEUDOS = 1;
const STEP_MATCH_MEET = 2;
const STEP_CONFIRMAR = 3;
const STEP_SUCESSO = 4;

const STEPS_META = [
  { key: 0, label: "Resumo", Icon: FaUser },
  { key: 1, label: "Conteúdos", Icon: FaCalendarAlt },
  { key: 2, label: "Match & Meet", Icon: FaHandshake },
  { key: 3, label: "Confirmar", Icon: FaClipboardList },
];

export default function CredenciamentoConteudos() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | invalid | valid
  const [mensagemErro, setMensagemErro] = useState("");
  const [convidado, setConvidado] = useState(null);
  const [step, setStep] = useState(STEP_RESUMO);
  const [selectedIds, setSelectedIds] = useState([]);
  const [empresasMatchMeet, setEmpresasMatchMeet] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [dias, setDias] = useState([]);
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);

  const sucesso = step === STEP_SUCESSO;

  useEffect(() => {
    let cancelado = false;
    (async () => {
      if (!token) {
        setStatus("invalid");
        setMensagemErro("Link inválido.");
        return;
      }
      setStatus("loading");
      try {
        const data = await validarTokenConteudos(token);
        if (cancelado) return;
        if (!data?.ok || !data?.dados) {
          setStatus("invalid");
          setMensagemErro(data?.mensagem || "Link inválido ou expirado.");
          return;
        }
        setConvidado(data.dados);
        setStatus("valid");
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setStatus("invalid");
        setMensagemErro("Não foi possível validar o link. Tente novamente.");
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [token]);

  useEffect(() => {
    if (status !== "valid") return;
    let cancelado = false;
    (async () => {
      setCarregandoAgenda(true);
      try {
        const data = await listarConteudosAgenda();
        if (cancelado) return;
        if (!data?.ok) {
          toast.error(data?.mensagem || "Não foi possível carregar os conteúdos.");
          setDias([]);
          setCatalogo([]);
          return;
        }
        setDias(data?.dados?.dias || []);
        setCatalogo(data?.dados?.catalogo || []);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        toast.error("Erro ao carregar a agenda de conteúdos.");
        setDias([]);
        setCatalogo([]);
      } finally {
        if (!cancelado) setCarregandoAgenda(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [status]);

  const handleConfirmar = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const regulares = idsConteudosRegulares(selectedIds, catalogo);
      const empresaInteresse = empresasMatchMeet
        .map((e) => (typeof e === "string" ? e : e?.nome))
        .filter(Boolean)
        .join(";");
      const data = await concluirEscolhaConteudos({
        token,
        conteudo1: regulares[0] ?? null,
        conteudo2: regulares[1] ?? null,
        empresaInteresse,
      });
      if (!data?.ok) {
        toast.error(data?.mensagem || "Não foi possível concluir.");
        if (data?.erro === "token_usado") {
          setStatus("invalid");
          setMensagemErro(data.mensagem);
        }
        return;
      }
      setStep(STEP_SUCESSO);
      toast.success(data.mensagem || "Escolhas registradas.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao concluir a escolha de conteúdos.");
    } finally {
      setIsSubmitting(false);
    }
  }, [token, selectedIds, empresasMatchMeet, catalogo]);

  if (status === "loading") {
    return (
      <div className="c26-page cc-page--center">
        <FaSpinner className="fa-spin" size={28} aria-hidden />
        <p>Validando seu link…</p>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="c26-page c26-page--invalid">
        <div className="cc-invalid-wrap">
          <TokenInvalido />
          {mensagemErro ? (
            <p className="cc-invalid-msg">{mensagemErro}</p>
          ) : null}
        </div>
      </div>
    );
  }

  const stepper = (
    <ol className="c26-stepper" aria-label="Etapas da escolha de conteúdos">
      {STEPS_META.map((s, i) => {
        const active = !sucesso && step === i;
        const done = sucesso || step > i;
        const upcoming = !sucesso && i > step;
        return (
          <li
            key={s.key}
            className={`c26-step${active ? " is-active" : ""}${
              done ? " is-done" : ""
            }${upcoming ? " is-upcoming" : ""}`}
            aria-current={active ? "step" : undefined}
          >
            <span className="c26-step-icon" aria-hidden="true">
              <s.Icon />
            </span>
            <span className="c26-step-label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="c26-page">
      <a href="#c26-main" className="c26-skip-link">
        Ir para o conteúdo
      </a>
      <div className="c26-banner-wrap">
        <img
          src={bannerSbf}
          alt="Saúde Business Fórum 2026 — Costa do Sauípe, Bahia"
          className="c26-banner"
          width={1100}
          height={220}
          decoding="async"
        />
      </div>

      <div className={`c26-layout${sucesso ? " c26-layout--success" : ""}`}>
        {!sucesso ? (
          <aside className="c26-aside" aria-label="Identidade visual">
            <p className="c26-aside-badge">
              Conteúdos 2026 · Costa do Sauípe
            </p>
            <figure className="c26-aside-figure">
              <img src={ilustBaiana} alt="" decoding="async" />
              <figcaption className="visually-hidden">
                Ilustração decorativa de boas-vindas
              </figcaption>
            </figure>
          </aside>
        ) : null}

        <main className="c26-main" id="c26-main">
          {!sucesso ? (
            <>
              <p className="c26-eyebrow">Convidado</p>
              <h1 className="c26-title">Escolha de conteúdos SBF 2026</h1>
              {stepper}

              {step === STEP_RESUMO ? (
                <PassoResumo
                  convidado={convidado}
                  onContinuar={() => setStep(STEP_CONTEUDOS)}
                />
              ) : null}

              {step === STEP_CONTEUDOS ? (
                <>
                  {carregandoAgenda ? (
                    <div className="c26-step-panel cc-page--center">
                      <FaSpinner className="fa-spin" size={24} aria-hidden />
                      <p>Carregando conteúdos…</p>
                    </div>
                  ) : (
                    <PassoConteudos
                      dias={dias}
                      catalogo={catalogo}
                      selectedIds={selectedIds}
                      onChangeSelected={setSelectedIds}
                    />
                  )}
                  <div className="c26-actions">
                    <button
                      type="button"
                      className="c26-btn c26-btn-ghost"
                      onClick={() => setStep(STEP_RESUMO)}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      className="c26-btn c26-btn-primary"
                      onClick={() => setStep(STEP_MATCH_MEET)}
                      disabled={carregandoAgenda}
                    >
                      Ir para Match &amp; Meet
                    </button>
                  </div>
                </>
              ) : null}

              {step === STEP_MATCH_MEET ? (
                <>
                  <PassoMatchMeet
                    empresas={empresasMatchMeet}
                    onChangeEmpresas={setEmpresasMatchMeet}
                  />
                  <div className="c26-actions">
                    <button
                      type="button"
                      className="c26-btn c26-btn-ghost"
                      onClick={() => setStep(STEP_CONTEUDOS)}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      className="c26-btn c26-btn-primary"
                      onClick={() => setStep(STEP_CONFIRMAR)}
                    >
                      Revisar escolhas
                    </button>
                  </div>
                </>
              ) : null}

              {step === STEP_CONFIRMAR ? (
                <PassoConfirmacao
                  convidado={convidado}
                  selectedIds={selectedIds}
                  empresas={empresasMatchMeet}
                  catalogo={catalogo}
                  isSubmitting={isSubmitting}
                  onVoltar={() => setStep(STEP_MATCH_MEET)}
                  onConfirmar={handleConfirmar}
                />
              ) : null}
            </>
          ) : (
            <div className="c26-success">
              <div className="c26-success-icon" aria-hidden="true">
                <FaCheck />
              </div>
              <h2 className="c26-title" style={{ marginBottom: "0.5rem" }}>
                Escolhas registradas
              </h2>
              <p className="c26-lead" style={{ margin: "0 auto 1rem" }}>
                Obrigado! Suas escolhas foram registradas e este link foi
                encerrado. Em caso de dúvida, fale com a organização do evento.
              </p>
              <div className="c26-success-footer-banner" aria-hidden="true">
                <img
                  src={footerCred2026}
                  alt=""
                  className="c26-success-footer-banner__img"
                  decoding="async"
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
